import { useQuery, useMutation } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { queryKeys, QUERY_CONFIG } from '../../lib/react-query'
import { queryClient } from '../../lib/react-query/queryClient'
import { WorkflowTable } from '../../app/modules/types/workflow'
import { OptionsType } from '../../app/modules/types/filters'
import { useAuthStore } from '../../store/authStore'
import { toast } from 'sonner'
import { workflowApi } from '../../services/api'

/**
 * Hook for fetching paginated workflow list
 * Automatically refetches when URL params change
 */
export function useWorkflow() {
  const orgId = useAuthStore((state) => state.orgId)
  const [searchParams] = useSearchParams()
  const params = searchParams.toString()

  return useQuery({
    queryKey: queryKeys.workflow.list(orgId, params),
    queryFn: (): Promise<WorkflowTable> => workflowApi.getList(params),
    enabled: !!orgId,
  })
}

/**
 * Hook for fetching workflow filter options
 * Cached for 15 minutes as these change less frequently
 */
export function useWorkflowFilterOptions() {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.workflow.filterOptions(orgId),
    queryFn: (): Promise<OptionsType> => workflowApi.getFilterOptions(),
    staleTime: QUERY_CONFIG.LISTS.staleTime,
    enabled: !!orgId,
  })
}

/**
 * Hook for fetching a single workflow by ID
 */
export function useWorkflowDetail(id: string | null) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.workflow.detail(orgId, id || ''),
    queryFn: () => workflowApi.getById(id!),
    enabled: !!id && !!orgId,
    // staleTime: 0,
    // gcTime: 0,
  })
}

/** Paginated version list of one workflow, newest first. */
export function useWorkflowVersions(id: string | null, page: number) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.workflow.versions(orgId, id || '', page),
    queryFn: () => workflowApi.getVersions(id!, page),
    enabled: !!id && !!orgId,
  })
}

/** One version's graph, read-only. A deleted workflow or unknown number is a 404. */
export function useWorkflowVersion(id: string | null, number: number | null) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.workflow.version(orgId, id || '', number ?? 0),
    queryFn: () => workflowApi.getVersion(id!, number!),
    enabled: !!id && number !== null && !!orgId,
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status === 404 || status === 403 || status === 400) return false
      return failureCount < 3
    },
  })
}

/**
 * Restore a version into the unsaved draft. Only the draft changes, so the
 * workflow's caches are flagged stale — the editor re-reads the draft on mount.
 */
export function useRestoreWorkflowVersion() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: ({
      workflowId,
      number,
      replace,
    }: {
      workflowId: string
      number: number
      replace?: boolean
    }) => workflowApi.restoreVersion(workflowId, number, replace),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflow.all(orgId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflow.detail(orgId, variables.workflowId) })
    },
  })
}

/**
 * Hook for fetching workflow node types
 */
export function useWorkflowNodeTypes() {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.workflow.nodeTypes(orgId),
    queryFn: () => workflowApi.getNodeTypes(),
    staleTime: QUERY_CONFIG.LISTS.staleTime,
    enabled: !!orgId,
  })
}

/**
 * Hook for fetching a single workflow node type by ID
 */
export function useWorkflowNodeTypeById(nodeTypeId: string | null) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: [orgId, 'workflow', 'nodeType', nodeTypeId || ''],
    queryFn: () => workflowApi.getNodeTypeById(nodeTypeId!),
    enabled: !!nodeTypeId && !!orgId,
    staleTime: QUERY_CONFIG.LISTS.staleTime,
  })
}

// ============================================
// WORKFLOW MUTATIONS
// ============================================

/**
 * Hook for creating a new workflow
 */
export function useCreateWorkflow() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (data: {
      name: string
      description: string
    }): Promise<any> => workflowApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflow.all(orgId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.allWorkflowsForScan(orgId) })
      toast.success('Workflow created successfully')
    },

  })
}

/**
 * Hook for updating a workflow
 */
export function useUpdateWorkflow() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: ({
      workflowId,
      data,
    }: {
      workflowId: string
      data: { name?: string; description?: string }
    }): Promise<any> => workflowApi.update(workflowId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflow.all(orgId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflow.detail(orgId, variables.workflowId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.allWorkflowsForScan(orgId) })
      toast.success('Workflow Edit successfully')
    },

  })
}

/**
 * Hook for deleting a workflow
 */
export function useDeleteWorkflow() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (workflowId: string): Promise<void> => workflowApi.delete(workflowId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflow.all(orgId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.allWorkflowsForScan(orgId) })
      toast.success('Workflow deleted successfully')
    },

  })
}

/**
 * Hook for saving workflow draft (nodes and connectors)
 */
export function useSaveWorkflowDraft() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: ({
      workflowId,
      nodes,
      connectors,
    }: {
      workflowId: string
      nodes: any[]
      connectors: any[]
    }): Promise<any> => workflowApi.saveDraft(workflowId, { nodes, connectors }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflow.all(orgId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflow.detail(orgId, variables.workflowId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.allWorkflowsForScan(orgId) })
    },

  })
}

/**
 * Hook for saving workflow (finalize)
 */
export function useSaveWorkflow() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: ({
      workflowId,
      nodes,
      connectors,
    }: {
      workflowId: string
      nodes: any[]
      connectors: any[]
    }): Promise<any> => workflowApi.save(workflowId, { nodes, connectors }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflow.all(orgId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflow.detail(orgId, variables.workflowId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.allWorkflowsForScan(orgId) })
      toast.success('Workflow saved successfully')
    },

  })
}

/**
 * Hook for discarding workflow changes
 */
export function useDiscardWorkflow() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (workflowId: string): Promise<any> => workflowApi.discard(workflowId),
    onSuccess: (_data) => {
      // queryClient.invalidateQueries({ queryKey: queryKeys.workflow.detail(orgId, variables) })
    },

  })
}

/**
 * Hook for generating workflow with AI
 */
export function useGenerateWorkflowAI() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (workflowId: string): Promise<any> => workflowApi.getAiAssistStatus(workflowId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflow.detail(orgId, variables) })
    },

  })
}

/**
 * Hook for testing a node configuration
 */
export function useTestNode() {
  return useMutation({
    mutationFn: (data: {
      node_type: string
      configuration: Record<string, any>
    }): Promise<any> => workflowApi.testNode(data),

  })
}

/**
 * Hook for validating node configuration against schema
 * Stateless validation - no cache invalidation needed
 */
export function useValidateNodeConfig() {
  return useMutation({
    mutationFn: (data: { component_id: string; config: Record<string, any> }): Promise<any> =>
      workflowApi.validateNodeConfig(data),
  })
}

/**
 * Hook for fetching AI assistant status for a node type
 * Auto-polls every 2s while status is 'pending'
 */
export function useNodeAiStatus(nodeTypeId: string | undefined, isEnable = true) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.workflow.nodeAi.status(orgId, nodeTypeId || ''),
    queryFn: () => workflowApi.getNodeAiStatus(nodeTypeId!),
    enabled: !!nodeTypeId && isEnable && !!orgId,
    refetchInterval: (query) => (query.state.data?.status === 'pending' ? 2000 : false), // Auto-poll while pending
    staleTime: 0, // Always fetch fresh when accessed
  })
}

/**
 * Hook for sending a message to the AI assistant for a node type
 */
export function useSendNodeAiMessage() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (data: { nodeTypeId: string; message: string }): Promise<any> =>
      workflowApi.sendNodeAiMessage(data.nodeTypeId, data.message),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflow.nodeAi.status(orgId, variables.nodeTypeId),
      })
    },

  })
}

/**
 * Hook for sending a message to the workflow AI assistant
 */
export function useSendWorkflowAiMessage() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (data: { workflowId: string; message: string }): Promise<any> =>
      workflowApi.sendWorkflowAiMessage(data.workflowId, data.message),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [orgId, 'workflow', 'ai-status', variables.workflowId],
      })
    },

  })
}

/**
 * Hook for fetching workflow AI status
 * Can be used with refetch for polling
 */
export function useWorkflowAiStatus(workflowId: string | null, enabled: boolean = false) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: [orgId, 'workflow', 'ai-status', workflowId || ''],
    queryFn: () => workflowApi.getWorkflowAiStatus(workflowId!),
    enabled: !!workflowId && enabled && !!orgId,
    refetchInterval: false, // Manual refetch via polling
    staleTime: 0,
  })
}
